/**
 * Resize handle for non-uniform scaling.
 *
 * Unlike ScaleHandle (uniform scaling), ResizeHandle allows:
 * - Free resize (both axes) at corners
 * - Single-axis resize at edges (horizontal or vertical only)
 *
 * Performance optimization: Uses the ODA view.add() pattern from BaseHandle.
 * Ghost entities are added directly to the view (not database) for fast
 * interactive rendering during drag operations.
 */

import type { DrawingModule } from "../../types/DrawingWeb";
import type { DRDrawingViewer } from "../../DRDrawingViewer";
import type { CommandHistory } from "../../commands/CommandHistory";
import type {
  TransformContext,
  KeyPoints,
  Point3D,
  ResizeAxes,
} from "../types";
import { BaseHandle } from "./BaseHandle";
import {
  HandleType,
  HandlePosition,
  HANDLE_SIZES,
  TRANSFORM_THEME,
} from "../types";
import { MeasurementLabels } from "../../overlay/MeasurementLabels";

/**
 * Handle for resizing entities (non-uniform scaling).
 */
export class ResizeHandle extends BaseHandle {
  readonly type = HandleType.Resize;
  readonly position: HandlePosition;

  /** The anchor point (opposite corner/edge) for resizing */
  private anchorPoint: Point3D;

  /** Axis constraints */
  private axes: ResizeAxes;

  private measureLabels: MeasurementLabels | null = null;

  constructor(
    viewer: DRDrawingViewer,
    history: CommandHistory,
    entityIds: DrawingModule.OdDbObjectId[],
    keyPoints: KeyPoints,
    position: HandlePosition,
    anchorPosition: HandlePosition,
    axes: ResizeAxes = { x: true, y: true },
  ) {
    super(viewer, history, entityIds, keyPoints, position);

    this.position = position;
    this.axes = axes;

    // Set anchor to specified position
    this.anchorPoint = this.getPositionFromKeyPoints(anchorPosition);

    // Override draw options for resize handle
    // Use slightly different color for edge vs corner handles
    const isEdge = !axes.x || !axes.y;
    this.drawOptions = {
      fillColor: isEdge
        ? TRANSFORM_THEME.handleFill
        : TRANSFORM_THEME.scaleHandle,
      strokeColor: TRANSFORM_THEME.handleStroke,
      radius:
        (isEdge ? HANDLE_SIZES.scale - 1 : HANDLE_SIZES.scale) *
        HANDLE_SIZES.handleScale,
      icon: "scale",
    };
  }

  /**
   * Update resize during drag.
   */
  ongoing(context: TransformContext): void {
    if (!this.isTransforming) return;

    // Calculate distances from anchor to handle (original) and to cursor (current)
    const originalDistanceX = this.worldPosition.x - this.anchorPoint.x;
    const originalDistanceY = this.worldPosition.y - this.anchorPoint.y;

    const currentDistanceX = context.worldPoint.x - this.anchorPoint.x;
    const currentDistanceY = context.worldPoint.y - this.anchorPoint.y;

    // Calculate scale ratios
    let scaleX = 1;
    let scaleY = 1;

    if (this.axes.x && Math.abs(originalDistanceX) > 0.0001) {
      scaleX = currentDistanceX / originalDistanceX;
    }

    if (this.axes.y && Math.abs(originalDistanceY) > 0.0001) {
      scaleY = currentDistanceY / originalDistanceY;
    }

    // Clamp to reasonable values and prevent flipping
    scaleX = Math.max(0.1, Math.min(10, scaleX));
    scaleY = Math.max(0.1, Math.min(10, scaleY));

    // Update accumulated scale values
    this.setAccumulated({
      scale: { x: scaleX, y: scaleY, z: 1 },
      scaleCenter: { ...this.anchorPoint },
    });

    // Apply to ghosts (fast - no recordGraphicsModified)
    this.setGhostTransforms();

    // Update view with throttling
    this.throttledUpdate();

    this.updateMeasurements(context, scaleX, scaleY);
  }

  private updateMeasurements(
    context: TransformContext,
    scaleX: number,
    scaleY: number,
  ): void {
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    if (!this.measureLabels) {
      this.measureLabels = new MeasurementLabels(
        overlay,
        this.viewer.getBackgroundColor(),
      );
    }

    // Current world dimensions = original * |scale|
    const originalWidth = Math.abs(this.keyPoints.max.x - this.keyPoints.min.x);
    const originalHeight = Math.abs(
      this.keyPoints.max.y - this.keyPoints.min.y,
    );
    const currentWidth = originalWidth * Math.abs(scaleX);
    const currentHeight = originalHeight * Math.abs(scaleY);

    // Screen positions: anchor is fixed, cursor represents the moving edge/corner
    const anchorScreen = this.viewer.worldToScreen(this.anchorPoint);
    const cursorScreen = context.screenPoint;

    // Determine min/max screen coords for label positioning
    const sMinX = Math.min(anchorScreen.x, cursorScreen.x);
    const sMaxX = Math.max(anchorScreen.x, cursorScreen.x);
    const sMinY = Math.min(anchorScreen.y, cursorScreen.y);
    const sMaxY = Math.max(anchorScreen.y, cursorScreen.y);

    // Width guide line + label (only if X axis is active)
    if (this.axes.x) {
      this.measureLabels.setLine("width", sMinX, sMinY, sMaxX, sMinY);
      this.measureLabels.setLabel(
        "width",
        (sMinX + sMaxX) / 2,
        sMinY - 18,
        MeasurementLabels.formatDistance(currentWidth),
      );
    }

    // Height guide line + label (only if Y axis is active)
    if (this.axes.y) {
      this.measureLabels.setLine("height", sMaxX, sMinY, sMaxX, sMaxY);
      this.measureLabels.setLabel(
        "height",
        sMaxX + 18,
        (sMinY + sMaxY) / 2,
        MeasurementLabels.formatDistance(currentHeight),
      );
    }
  }

  /**
   * End resize - execute command for undo/redo.
   */
  end(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    super.end();
  }

  /**
   * Cancel resize - restore original state.
   */
  cancel(): void {
    this.measureLabels?.clear();
    this.measureLabels = null;
    super.cancel();
  }
}
